import { useCallback, useEffect, useMemo, useState } from 'react'
import { NormalStep, StepPersistanceMode } from './types.d'
import { OnboardingStepKey } from '@cbgms/api'

interface usePersistDecoratorOutput {
  steps: NormalStep<any>[]
  isPersisting: boolean
}

export const usePersistDecorator = (
  steps: NormalStep<any>[],
  mode: StepPersistanceMode,
  onFinish?: (stepsThatError: OnboardingStepKey[]) => void
): usePersistDecoratorOutput => {
  // here we'll determine how and when to persist steps.
  // we keep track of which persister has ran.
  // we also need to trigger these using useEffects to be sure that the submit has fully committed their state
  const [isPersisting, setIsPersisting] = useState<boolean>(false)
  const [stepsToTrigger, setStepsToTrigger] = useState<string[]>([])
  const [stepsThatRan, setStepsThatRan] = useState<string[]>([])
  const runPersister = useCallback(
    (step: NormalStep<unknown>) => {
      setStepsToTrigger([...new Set([...stepsToTrigger, step.step])])
    },
    [steps]
  )
  const runAllPersister = useCallback(() => {
    setStepsToTrigger(steps.map(s => s.step))
  }, [steps])

  // run the actual persisters whenever a new step should run
  useEffect(() => {
    const stepsThatError: OnboardingStepKey[] = []
    const RunPersisters = async () => {
      // flag that these have ran
      setStepsThatRan(stepsToTrigger)

      const stepsToRun = [...steps]
        .filter(s => !stepsThatRan.find(hasRan => hasRan === s.step) && !!stepsToTrigger.find(toRun => toRun === s.step))
        .sort((p1, p2) => p1.persister.persistOrder - p2.persister.persistOrder)
      // run the persisters that have not ran already
      if (stepsToRun.length) {
        setIsPersisting(true)
      }
      for (const step of stepsToRun) {
        try {
          await step.persister.onPersist()
        } catch (e) {
          // continue when 1 persister gave an error. we still want to run the other ones
          stepsThatError.push(step.stepData.Key)
          console.error('Error persisting:', e)
        }
      }

      if (stepsToRun.length) {
        setIsPersisting(false)
      }
      // trigger the finisher only in threads that actually did an api request
      const allStepsRan =
        stepsToRun.length && !steps.find(a => ![...stepsThatRan, ...stepsToRun.map(ran => ran.step)].find(ranStep => ranStep === a.step))
      if (allStepsRan) {
        onFinish?.(stepsThatError)
      }
    }
    RunPersisters()
  }, [steps, stepsToTrigger, setStepsThatRan, stepsThatRan, setIsPersisting])

  if (mode === StepPersistanceMode.PersistInOneTransaction && !!steps.length) {
    // decorate the last step with the persistAll
    const lastStepUnwrappedOnSubmit = steps[steps.length - 1].stepProps.onSubmit
    steps[steps.length - 1].stepProps.onSubmit = (args: unknown) => {
      lastStepUnwrappedOnSubmit?.(args)
      runAllPersister()
    }
  }
  if (mode === StepPersistanceMode.PersistImmediately) {
    // decorate each step to immediatly persist
    steps.forEach(s => {
      const origStepSubmit = s.stepProps.onSubmit
      s.stepProps.onSubmit = (args: unknown) => {
        origStepSubmit(args)
        runPersister(s)
      }
    })
  }

  return useMemo(() => ({ steps, isPersisting }), [isPersisting, steps])
}
